import React, { useState } from 'react';
import { Button, Card, DatePicker, Input, Select, Space, Typography } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import {
  ALIMTALK_SEND_DATE_BASIS_OPTIONS,
  ALIMTALK_SEND_SEARCH_TYPE_OPTIONS,
  ALIMTALK_SEND_STATUS_FILTER_OPTIONS,
  ALIMTALK_SEND_TYPE_FILTER_OPTIONS,
} from '../../constants';
import { AlimtalkSendHistoryFilter, isInvalidScheduleIdSearch } from '../../utils';
import { AlimtalkSendHistorySearchType, AlimtalkSendStatus, AlimtalkSendType } from '../../types';

const { RangePicker } = DatePicker;

interface Props {
  /** 초기화 버튼이 돌아갈 조건 */
  defaultFilter: AlimtalkSendHistoryFilter;
  /** 처음 보여줄 조건 (URL 에서 복원한 조회 조건) — 없으면 defaultFilter */
  initialFilter?: AlimtalkSendHistoryFilter;
  onSearch: (filter: AlimtalkSendHistoryFilter) => void;
  onReset: () => void;
}

const FIELD_LABEL_WIDTH = 72;

const SCHEDULE_ID_INVALID_MESSAGE = '예약 ID는 숫자(1 이상의 정수)로 입력해 주세요.';

const Field = ({ label, children }: { label: string; children: React.ReactNode }): JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ width: FIELD_LABEL_WIDTH, color: '#676a6c', fontWeight: 500 }}>{label}</span>
    {children}
  </div>
);

/** 알림톡 발송 내역 검색 필터 */
const AlimtalkSendHistorySearchBox = ({ defaultFilter, initialFilter, onSearch, onReset }: Props): JSX.Element => {
  const [filter, setFilter] = useState<AlimtalkSendHistoryFilter>(initialFilter ?? defaultFilter);

  const handleChange = <K extends keyof AlimtalkSendHistoryFilter>(
    key: K,
    value: AlimtalkSendHistoryFilter[K],
  ): void => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  const isSearchValueInvalid = isInvalidScheduleIdSearch(filter);

  /**
   *  잘못된 예약 ID 는 요청을 보내지 않고 입력란 아래 안내로 막는다.
   *  같은 조건 재검색 시 refetch 는 상위(onSearch)가 처리한다.
   */
  const handleSearch = (): void => {
    if (isSearchValueInvalid) return;
    onSearch(filter);
  };

  const handleReset = (): void => {
    setFilter(defaultFilter);
    onReset();
  };

  const handleRangeChange: RangePickerProps['onChange'] = (dates) => {
    handleChange('dateRange', dates ? [dates[0], dates[1]] : null);
  };

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Space size="large" wrap>
          <Field label="조회기준">
            <Select
              style={{ width: 140 }}
              value={filter.dateBasis}
              options={ALIMTALK_SEND_DATE_BASIS_OPTIONS}
              onChange={(value) => handleChange('dateBasis', value)}
            />
          </Field>
          <Field label="예약일시">
            <RangePicker value={filter.dateRange} onChange={handleRangeChange} allowEmpty={[true, true]} />
          </Field>
        </Space>

        <Space size="large" wrap>
          <Field label="상태">
            <Select
              style={{ width: 160 }}
              value={filter.status}
              options={ALIMTALK_SEND_STATUS_FILTER_OPTIONS}
              onChange={(value) => handleChange('status', value as AlimtalkSendStatus | '')}
            />
          </Field>
          <Field label="발송유형">
            <Select
              style={{ width: 180 }}
              value={filter.sendType}
              options={ALIMTALK_SEND_TYPE_FILTER_OPTIONS}
              onChange={(value) => handleChange('sendType', value as AlimtalkSendType | '')}
            />
          </Field>
        </Space>

        <Space size="large" wrap>
          <Field label="검색">
            <Space.Compact>
              <Select
                style={{ width: 120 }}
                value={filter.searchType}
                options={ALIMTALK_SEND_SEARCH_TYPE_OPTIONS}
                onChange={(value) => handleChange('searchType', value as AlimtalkSendHistorySearchType)}
              />
              <Input
                allowClear
                style={{ width: 260 }}
                placeholder={filter.searchType === 'scheduleId' ? '예약 ID(숫자) 입력' : '검색어 입력'}
                status={isSearchValueInvalid ? 'error' : undefined}
                value={filter.searchValue}
                onChange={(event) => handleChange('searchValue', event.target.value)}
                onPressEnter={handleSearch}
                maxLength={500}
              />
            </Space.Compact>
          </Field>
          {isSearchValueInvalid ? <Typography.Text type="danger">{SCHEDULE_ID_INVALID_MESSAGE}</Typography.Text> : null}
        </Space>

        <Space>
          <Button type="primary" onClick={handleSearch} disabled={isSearchValueInvalid}>
            검색
          </Button>
          <Button onClick={handleReset}>초기화</Button>
        </Space>
      </Space>
    </Card>
  );
};

export default AlimtalkSendHistorySearchBox;
